import { useId, type ReactNode } from 'react';
import { inputClass } from '../ui';

/** Labelled range + number input (bounded; keyboard accessible). */
export function NumberControl({
  label,
  value,
  min,
  max,
  step = 1,
  onChange,
  testId,
  hint,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  step?: number;
  onChange: (v: number) => void;
  testId?: string;
  hint?: string;
}) {
  const id = useId();
  const commit = (raw: string) => {
    const v = Number(raw);
    if (!Number.isFinite(v)) return;
    onChange(Math.min(max, Math.max(min, step >= 1 ? Math.round(v) : v)));
  };
  return (
    <div className="flex flex-col gap-1 text-xs text-zinc-400">
      <label htmlFor={id} className="flex justify-between gap-2">
        <span>{label}</span>
        {hint && <span className="text-zinc-400">{hint}</span>}
      </label>
      <div className="flex items-center gap-2">
        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={value}
          onChange={(e) => commit(e.target.value)}
          className="min-w-0 flex-1 accent-violet-500"
          aria-hidden="true"
          tabIndex={-1}
        />
        <input id={id} type="number" min={min} max={max} step={step} value={Number(value.toFixed(3))} onChange={(e) => commit(e.target.value)} className={`${inputClass} w-24`} data-testid={testId} />
      </div>
    </div>
  );
}

export function SelectControl<T extends string>({ label, value, options, onChange, testId }: { label: string; value: T; options: readonly (readonly [T, string])[]; onChange: (v: T) => void; testId?: string }) {
  const id = useId();
  return (
    <label htmlFor={id} className="flex flex-col gap-1 text-xs text-zinc-400">
      {label}
      <select id={id} className={inputClass} value={value} onChange={(e) => onChange(e.target.value as T)} data-testid={testId}>
        {options.map(([v, l]) => (
          <option key={v} value={v}>
            {l}
          </option>
        ))}
      </select>
    </label>
  );
}

export function ColorControl({ label, value, onChange, testId }: { label: string; value: string; onChange: (v: string) => void; testId?: string }) {
  const id = useId();
  return (
    <label htmlFor={id} className="flex items-center justify-between gap-2 text-xs text-zinc-400">
      {label}
      <input id={id} type="color" value={value} onChange={(e) => onChange(e.target.value)} className="h-8 w-14 cursor-pointer rounded border border-zinc-700 bg-zinc-900" data-testid={testId} />
    </label>
  );
}

export function CheckControl({ label, checked, onChange, testId }: { label: string; checked: boolean; onChange: (v: boolean) => void; testId?: string }) {
  return (
    <label className="flex items-center gap-2 text-xs text-zinc-300">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} data-testid={testId} />
      {label}
    </label>
  );
}

export function Fieldset({ legend, children }: { legend: string; children: ReactNode }) {
  return (
    <fieldset className="flex flex-col gap-2 rounded-md border border-zinc-800 p-2">
      <legend className="px-1 text-xs font-semibold tracking-wide text-zinc-300 uppercase">{legend}</legend>
      {children}
    </fieldset>
  );
}
